import React, { useEffect, useState } from 'react';
import { Card, Typography, Spin, Alert, Avatar, Tag } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, BankOutlined } from '@ant-design/icons';
import branchService, { Branch } from '../services/branchService';
import branchManagerService, { BranchManager } from '../services/branchManagerService';

const { Title, Text } = Typography;

interface BranchProfileProps {
  branchId?: number;
  managerId?: number;
}

const BranchProfile: React.FC<BranchProfileProps> = ({ branchId, managerId }) => {
  const [manager, setManager] = useState<BranchManager | null>(null);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!managerId) {
          setError('No manager ID available. Please ensure you are logged in correctly.');
          setLoading(false);
          return;
        }

        // Fetch manager data
        console.log('Fetching manager with ID:', managerId);
        const managerResponse = await branchManagerService.getBranchManagerById(managerId);
        const managerData = managerResponse.data.attributes;
        console.log('Manager data received:', managerData);
        setManager(managerData);

        // Fetch branch data - try branchId first, then get all branches
        if (branchId) {
          try {
            console.log('Fetching branch with ID:', branchId);
            const branchResponse = await branchService.getBranchById(branchId);
            const branchData = branchResponse.data.attributes;
            console.log('Branch data received:', branchData);
            setBranch(branchData);
          } catch (branchErr) {
            console.log('Branch ID not found, fetching all branches...');
            // If specific branch not found, get all branches and use first one
            const branchesResponse = await branchService.getBranches();
            const branches = branchesResponse.data;
            if (branches && branches.length > 0) {
              console.log('Using first available branch:', branches[0]);
              setBranch(branches[0].attributes);
            }
          }
        } else {
          // No branchId provided, get all branches and use first one
          console.log('No branchId provided, fetching all branches...');
          const branchesResponse = await branchService.getBranches();
          const branches = branchesResponse.data;
          if (branches && branches.length > 0) {
            console.log('Using first available branch:', branches[0]);
            setBranch(branches[0].attributes);
          }
        }
      } catch (err: any) {
        console.error('Error fetching profile data:', err);
        setError(err.response?.data?.message || 'Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [managerId, branchId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading manager profile...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '16px 0' }}
      />
    );
  }

  if (!manager) {
    return (
      <Alert
        message="No Data"
        description="No manager profile data available"
        type="warning"
        showIcon
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Title level={5} style={{ margin: '0 0 16px 0', color: '#1890ff' }}>
        Manager Profile
      </Title>

      {/* Manager Information */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#52c41a', marginRight: '12px' }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>{manager.manager_name || 'Unknown Manager'}</Title>
            <Tag color={manager.is_active ? 'green' : 'red'}>
              {manager.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </div>
        </div>

        <div style={{ marginLeft: '48px' }}>
          {/* Contact Information */}
          <div style={{ marginBottom: '12px' }}>
            <Text strong style={{ color: '#52c41a' }}>Contact Information</Text>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Username: </Text>
            <Text>{manager.username || 'Not provided'}</Text>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <PhoneOutlined style={{ marginRight: '8px', color: '#666' }} />
            <Text>{manager.phone || 'No phone available'}</Text>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <MailOutlined style={{ marginRight: '8px', color: '#666' }} />
            <Text>{manager.email || 'No email available'}</Text>
          </div>


          {/* System Information */}
          <div style={{ marginBottom: '12px', marginTop: '16px' }}>
            <Text strong style={{ color: '#52c41a' }}>System Information</Text>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Created: </Text>
            <Text>{new Date(manager.createdAt).toLocaleDateString()}</Text>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Last Updated: </Text>
            <Text>{new Date(manager.updatedAt).toLocaleDateString()}</Text>
          </div>

          {manager.last_login && (
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Last Login: </Text>
              <Text>{new Date(manager.last_login).toLocaleDateString()}</Text>
            </div>
          )}
        </div>
      </Card>

      {/* Branch Information */}
      {branch && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <Avatar icon={<BankOutlined />} style={{ backgroundColor: '#1890ff', marginRight: '12px' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>{branch.branch_name || 'Unknown Branch'}</Title>
              <Tag color={branch.is_active ? 'green' : 'red'}>
                {branch.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </div>
          </div>

          <div style={{ marginLeft: '48px' }}>
            {/* Contact Information */}
            <div style={{ marginBottom: '12px' }}>
              <Text strong style={{ color: '#1890ff' }}>Branch Information</Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <EnvironmentOutlined style={{ marginRight: '8px', color: '#666' }} />
              <Text>{branch.address || 'No address available'}</Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <PhoneOutlined style={{ marginRight: '8px', color: '#666' }} />
              <Text>{branch.phone || 'No phone available'}</Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <MailOutlined style={{ marginRight: '8px', color: '#666' }} />
              <Text>{branch.email || 'No email available'}</Text>
            </div>

            {branch.branch_type && (
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Type: </Text>
                <Tag color="blue">{branch.branch_type}</Tag>
              </div>
            )}

            {branch.tax_id && (
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Tax ID: </Text>
                <Text copyable={{ text: branch.tax_id }}>{branch.tax_id}</Text>
              </div>
            )}

            {branch.license_number && (
              <div style={{ marginBottom: '8px' }}>
                <Text strong>License: </Text>
                <Text copyable={{ text: branch.license_number }}>{branch.license_number}</Text>
              </div>
            )}

            {/* System Information */}
            <div style={{ marginBottom: '12px', marginTop: '16px' }}>
              <Text strong style={{ color: '#1890ff' }}>System Information</Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Created: </Text>
              <Text>{new Date(branch.createdAt).toLocaleDateString()}</Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Last Updated: </Text>
              <Text>{new Date(branch.updatedAt).toLocaleDateString()}</Text>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BranchProfile;